import React, { memo, useCallback, useRef, useState, useEffect, useMemo } from 'react';
import { logger } from '@/lib/logger';
import { sanitizeSVG } from '@/lib/sanitize';
import { unescapeSvg, resizeSvg } from '@/lib/svgNormalizer';
import { useAppStore } from '@/stores/appStore';
import { useFloorPlanContext } from '../FloorPlanProvider';
import { cn } from '@/lib/utils';
import type { LVProduct } from '@/features/product-library/types';
import type { MarkerSizesConfig } from '@/types/floorPlanViewer';
import { CategoryIconRenderer } from '@/features/product-library/utils/categoryIcons';
import { categoryTemplateService } from '@/services/categoryTemplateService';
import { organizationCategoryService } from '@/features/product-library/services/organizationCategoryService';
import { lvProductService } from '@/features/product-library/services/lvProductService';
import { toast } from 'sonner';
import { useUndoRedoStore } from '@/stores/undoRedoStore';
import { ProductImageCallout } from './ProductImageCallout';
import { useProduct } from '@/features/product-library/hooks/useProductLibrary';
import {
  useMergedCategories,
  type MergedCategory,
} from '@/features/product-library/hooks/useMergedCategories';

type DraggedEntityType = 'fixture' | 'control' | 'panel' | 'room' | 'product';

interface DragState {
  type: DraggedEntityType;
  id: string;
  startPos: { x: number; y: number };
  currentPos: { x: number; y: number };
  hasMovedThreshold?: boolean;
}

interface LVProductLayerProps {
  scale: number;
  onStartDrag?: (type: DraggedEntityType, id: string, e: React.MouseEvent) => void;
  isDragging?: boolean;
  dragState?: DragState | null;
  wasRecentlyDragged?: (elementId: string) => boolean;
  isPublicView?: boolean;
}

type ProductIconData = {
  svg?: string;
  url?: string;
  color?: string;
  preserveColors?: boolean;
} | null;

// Helper to generate abbreviation-based SVG icon for organization categories
const generateAbbreviationSvg = (
  abbreviation: string,
  fillColor: string,
  borderColor: string,
  textColor: string,
  shape: 'square' | 'circle' = 'square',
): string => {
  const shapeElement =
    shape === 'circle'
      ? `<circle cx="12" cy="12" r="10" fill="${fillColor}" stroke="${borderColor}" stroke-width="2"/>`
      : `<rect x="2" y="2" width="20" height="20" rx="2" fill="${fillColor}" stroke="${borderColor}" stroke-width="2"/>`;

  return `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">${shapeElement}<text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="10" font-weight="600" fill="${textColor}">${abbreviation}</text></svg>`;
};

const categoryKey = (category?: string | null, subcategory?: string | null) =>
  `${category || ''}:${subcategory || ''}`;

const getMergedCategoryIconData = (category: MergedCategory): ProductIconData => {
  if (category.icon_svg) {
    return {
      svg: category.icon_svg,
      url: undefined,
      color: category.fill_color,
      preserveColors: !category.is_system,
    };
  }

  if (category.icon_type === 'abbreviation' && category.abbreviation) {
    return {
      svg: generateAbbreviationSvg(
        category.abbreviation,
        category.fill_color || '#6B7280',
        category.border_color || '#374151',
        category.text_color || '#FFFFFF',
        category.icon_shape || 'square',
      ),
      url: undefined,
      color: category.fill_color || '#6B7280',
      preserveColors: true,
    };
  }

  return null;
};

// Component to render product icon with subcategory support
const ProductIcon = ({
  product,
  size = 18,
  className = '',
  applyHighContrastFilter = false,
  preloadedIconData,
}: {
  product: LVProduct;
  size?: number;
  className?: string;
  applyHighContrastFilter?: boolean; // Apply brightness/invert filter for high contrast mode (system templates only)
  preloadedIconData?: ProductIconData;
}) => {
  const [iconData, setIconData] = useState<ProductIconData>(preloadedIconData ?? null);
  const [isLoading, setIsLoading] = useState(!preloadedIconData);

  // Get categoryIconVersion from context to trigger re-fetch when org categories are updated
  const { categoryIconVersion } = useFloorPlanContext();

  // Use joined product_library data when available so floor-plan icons can render immediately.
  // Fall back to fetching only for older cache entries that do not include the join.
  const joinedProduct = product.product;
  const { data: fetchedProduct } = useProduct(
    joinedProduct ? undefined : product.product_id || undefined,
  );
  const linkedProduct = joinedProduct || fetchedProduct;

  useEffect(() => {
    const loadIconData = async () => {
      // Reset state when product changes
      setIconData(preloadedIconData ?? null);
      setIsLoading(!preloadedIconData);

      // Check if this is a split item - splits should use category-based icons
      // Split items are aliases of a parent product but need their own category icon
      // Use Boolean() to handle both boolean and truthy string values
      const isSplitItem = Boolean(product.metadata?.is_split);

      // PRIORITY 1: Check for custom icon from linked product_library entry
      // Custom icons take precedence over category icons
      // BUT skip for split items - they should use their assigned category's icon
      if (linkedProduct && !isSplitItem) {
        // Check for custom SVG icon
        if (linkedProduct.custom_icon_svg) {
          setIconData({
            svg: linkedProduct.custom_icon_svg,
            url: undefined,
            color: undefined,
            preserveColors: true, // Custom icons preserve their colors
          });
          setIsLoading(false);
          return;
        }

        // Check for custom abbreviation icon with styling
        if (linkedProduct.custom_icon_abbreviation) {
          const customSvg = generateAbbreviationSvg(
            linkedProduct.custom_icon_abbreviation,
            linkedProduct.custom_icon_fill_color || '#6B7280',
            linkedProduct.custom_icon_border_color || '#374151',
            linkedProduct.custom_icon_text_color || '#FFFFFF',
            (linkedProduct.custom_icon_shape as 'circle' | 'square') || 'square',
          );
          setIconData({
            svg: customSvg,
            url: undefined,
            color: linkedProduct.custom_icon_fill_color || '#6B7280',
            preserveColors: true, // Custom icons preserve their colors
          });
          setIsLoading(false);
          return;
        }
      }

      if (preloadedIconData) {
        setIsLoading(false);
        return;
      }

      // PRIORITY 2: Fall back to category/subcategory icons
      // Floor plan icons should show category/subcategory icons when no custom icon is set
      // Product images are shown in other contexts (drawers, product library, etc.)
      // This ensures consistent visual language on floor plans

      // Determine domain based on category - check if it's a lighting fixture category
      // Handle Controls category - now database-driven like fixtures and LV products
      if (
        product.category?.toLowerCase() === 'controls' ||
        product.category?.toLowerCase() === 'control'
      ) {
        // Controls are now in the database under 'lighting_controls' domain
        // Convert subcategory to slug format (e.g., "Centralized Keypad" -> "centralized-keypad")
        const subcategorySlug = product.subcategory
          ? product.subcategory.toLowerCase().replace(/\s+/g, '-')
          : undefined;

        try {
          // Try to load from database first (subcategory if present)
          const template = await categoryTemplateService.getByPath(
            'lighting_controls',
            'control',
            subcategorySlug,
          );
          if (template?.icon_svg) {
            setIconData({
              svg: template.icon_svg,
              url: undefined,
              color: template.default_color || '#8B5CF6',
              preserveColors: true, // Preserve control icon colors
            });
            setIsLoading(false);
            return;
          }
        } catch (error) {
          logger.debug('Failed to load control icon from database:', error);
        }

        // Fallback for Controls without database template - generic purple C icon
        setIconData({
          svg: '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="2" y="2" width="20" height="20" stroke="#8B5CF6" stroke-width="2" fill="none"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="14" font-weight="600" fill="#8B5CF6">C</text></svg>',
          url: undefined,
          color: '#8B5CF6',
          preserveColors: true,
        });
        setIsLoading(false);
        return;
      }

      const isLightingCategory = [
        'fixture',
        'lighting',
        'chandelier',
        'pendant',
        'recessed',
        'track',
        'wall_sconce',
      ].includes(product.category);
      const domain = isLightingCategory ? 'lighting_fixtures' : 'lv_products';

      // First try organization categories (custom categories)
      try {
        const orgCategory = await organizationCategoryService.getByPath(
          domain,
          product.category,
          product.subcategory || undefined,
        );
        if (orgCategory) {
          // Generate SVG from org category data (abbreviation-based icon)
          const iconSvg =
            orgCategory.icon_svg ||
            generateAbbreviationSvg(
              orgCategory.abbreviation,
              orgCategory.fill_color,
              orgCategory.border_color,
              orgCategory.text_color,
              orgCategory.icon_shape,
            );
          setIconData({
            svg: iconSvg,
            url: undefined,
            color: orgCategory.fill_color,
            preserveColors: true, // Don't replace colors for custom org categories
          });
          setIsLoading(false);
          return;
        }
      } catch (error) {
        // Organization categories may not be available (e.g., unauthenticated)
        logger.debug('Org category lookup failed (possibly unauthenticated):', error);
      }

      // Fall back to system category templates
      if (product.subcategory) {
        try {
          const template = await categoryTemplateService.getByPath(
            domain,
            product.category,
            product.subcategory,
          );
          if (template) {
            setIconData({
              svg: template.icon_svg || undefined,
              url: template.icon_url || undefined,
              color: template.default_color,
            });
            setIsLoading(false);
            return;
          }
        } catch (error) {
          logger.error('Failed to load subcategory icon:', error);
        }
      }

      // Fall back to parent category
      try {
        const template = await categoryTemplateService.getByPath(
          domain,
          product.category,
          undefined,
        );
        if (template) {
          setIconData({
            svg: template.icon_svg || undefined,
            url: template.icon_url || undefined,
            color: template.default_color,
          });
        }
      } catch (error) {
        logger.error('Failed to load category icon:', error);
      }

      // Mark loading as complete
      setIsLoading(false);
    };

    loadIconData();
  }, [
    product.category,
    product.subcategory,
    product.product_id,
    product.metadata?.is_split,
    preloadedIconData,
    linkedProduct,
    linkedProduct?.custom_icon_svg,
    linkedProduct?.custom_icon_abbreviation,
    categoryIconVersion,
  ]);

  // If we have icon data from database, use it
  if (iconData) {
    if (iconData.svg) {
      // For Controls category or custom org categories, preserve the hardcoded colors in the SVG
      const isControls = product.category?.toLowerCase() === 'controls';
      const shouldPreserveColors = isControls || iconData.preserveColors;

      // Clean and resize the SVG - normalization happens at upload time
      const cleanedSvg = unescapeSvg(iconData.svg);
      const processedSvg = resizeSvg(cleanedSvg, size, size);
      const targetColor = iconData.color || '#6B7280';

      // Determine the filter to apply:
      // - For org categories (preserveColors), never apply filter - show exact colors
      // - For system templates in high contrast mode, apply brightness/invert to make icon visible on colored bg
      const filterStyle = shouldPreserveColors
        ? undefined // Org categories: preserve exact colors
        : applyHighContrastFilter
          ? product.category === 'speaker'
            ? 'brightness(0)' // Make speaker icon black on orange bg
            : 'invert(1) brightness(2)' // Make others white on colored bg
          : undefined;

      return (
        <div
          className={className}
          style={{
            width: size,
            height: size,
            color: targetColor, // CSS currentColor inheritance for dynamic colors
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            ...(filterStyle ? { filter: filterStyle } : {}),
          }}
          data-preserve-colors={shouldPreserveColors ? 'true' : undefined}
          dangerouslySetInnerHTML={{
            __html: sanitizeSVG(processedSvg),
          }}
        />
      );
    }

    if (iconData.url) {
      return (
        <img
          src={iconData.url}
          alt={product.name || product.category}
          className={className}
          style={{
            width: size,
            height: size,
          }}
        />
      );
    }
  }

  if (isLoading) {
    return null;
  }

  // If loading is complete but no iconData found, fall back to CategoryIconRenderer
  // This ensures products without database templates still show an icon
  return <CategoryIconRenderer category={product.category} size={size} className={className} />;
};

export const LVProductLayer = memo(
  ({
    scale,
    onStartDrag,
    isDragging = false,
    dragState,
    wasRecentlyDragged,
    isPublicView,
  }: LVProductLayerProps) => {
    const selectedProducts = useAppStore(state => state.selection.products || new Set());
    const hoveredEntityId = useAppStore(state => state.ui.hoveredEntityId);
    const hoveredEntityType = useAppStore(state => state.ui.hoveredEntityType);
    const selectEntity = useAppStore(state => state.selectEntity);
    const setHoveredEntity = useAppStore(state => state.setHoveredEntity);
    const setActiveDrawer = useAppStore(state => state.setActiveDrawer);
    const setActiveAnnotationType = useAppStore(state => state.setActiveAnnotationType);
    const setActiveMode = useAppStore(state => state.setActiveMode);
    const activeTool = useAppStore(state => state.ui.toolbar.activeTool);
    const showProductLabels = useAppStore(state => state.products.showLabels);
    const iconStyle = useAppStore(state => state.products.iconStyle);
    const presentationLVProductIds = useAppStore(state => state.ui.presentationLVProductIds);
    const addAction = useUndoRedoStore(state => state.addAction);

    // Rotation state
    const [rotatingProductId, setRotatingProductId] = useState<string | null>(null);
    const [currentRotationAngle, setCurrentRotationAngle] = useState<number>(0);
    const rotationRef = useRef<{
      startAngle: number;
      startRotation: number;
      lastAngle: number;
      totalDelta: number;
    } | null>(null);

    // Get products data from the floor plan context
    const { data, temporaryProductLabelFontSize } = useFloorPlanContext();
    const products = data.lvProducts || [];
    const { data: lvCategories = [] } = useMergedCategories('lv_products');
    const lvIconDataByPath = useMemo(() => {
      const iconMap = new Map<string, ProductIconData>();

      lvCategories.forEach(category => {
        const iconData = getMergedCategoryIconData(category);
        if (iconData) {
          iconMap.set(categoryKey(category.category, category.subcategory), iconData);
        }
      });

      return iconMap;
    }, [lvCategories]);

    const getPreloadedProductIcon = useCallback(
      (product: LVProduct): ProductIconData =>
        lvIconDataByPath.get(categoryKey(product.category, product.subcategory)) ||
        lvIconDataByPath.get(categoryKey(product.category, null)) ||
        null,
      [lvIconDataByPath],
    );

    // Calculate renderable products (filtered, same logic as render)
    const renderableProducts = useMemo(() => {
      return products
        .filter(product => !product.parent_group_id)
        .filter(product => {
          const category = product.category?.toLowerCase();
          return category !== 'control' && category !== 'controls';
        });
    }, [products]);

    // Get LV products base scale from floor plan metadata (auto-calculated from image dimensions)
    // Default to 1.0 for existing floor plans without auto-scale applied
    const markerSizes = data.floorPlanMetadata?.marker_sizes as MarkerSizesConfig | null;
    const lvProductsBaseScale = markerSizes?.lv_products_base_scale ?? 1.0;
    // Use temporary font size during slider drag, fallback to saved value
    const productLabelFontSize =
      temporaryProductLabelFontSize ?? markerSizes?.product_label_font_size ?? 10;

    // Get multi-selection state
    const selectedFixtures = useAppStore(state => state.selection.fixtures);
    const selectedControls = useAppStore(state => state.selection.controls);
    const selectedAnnotations = useAppStore(state => state.selection.annotations);
    const selectedRooms = useAppStore(state => state.selection.rooms);
    const totalSelections =
      selectedProducts.size +
      selectedFixtures.size +
      selectedControls.size +
      selectedAnnotations.size +
      selectedRooms.size;
    const isMultiSelection = totalSelections > 1;
    const toggleEntitySelection = useAppStore(state => state.toggleEntitySelection);

    // Touch handling refs
    const touchTimers = useRef<Map<string, NodeJS.Timeout>>(new Map());
    const touchStartPositions = useRef<Map<string, { x: number; y: number }>>(new Map());

    // Handle product click
    const handleProductClick = useCallback(
      (product: LVProduct, e: React.MouseEvent) => {
        e.stopPropagation();

        // In public view, don't allow selection
        if (isPublicView) return;

        // Don't handle clicks if product is being rotated
        const productElement = e.currentTarget as HTMLElement;
        if (productElement?.dataset?.rotating === 'true') {
          return;
        }

        // Don't handle clicks if we recently dragged this element
        if (wasRecentlyDragged && wasRecentlyDragged(product.id)) {
          return;
        }

        // Handle selection
        if (e.shiftKey) {
          // Multi-select with shift key
          toggleEntitySelection('product', product.id);
          // Check if we now have multiple selections
          const newTotalSelections = totalSelections + (selectedProducts.has(product.id) ? -1 : 1);

          // Only skip drawer if ALL selections are annotations
          const state = useAppStore.getState();
          const annotationCount = state.selection.annotations.size;
          const hasOnlyAnnotations = annotationCount > 0 && annotationCount === newTotalSelections;

          if (newTotalSelections > 1 && !hasOnlyAnnotations) {
            setActiveDrawer('selection');
          }
        } else if (isMultiSelection && selectedProducts.has(product.id)) {
          // Already selected in multi-selection, just toggle it
          toggleEntitySelection('product', product.id);
        } else {
          // Single select
          selectEntity('product', product.id);

          // Clear any active placement tools when clicking an element
          setActiveAnnotationType(null);
          setActiveMode('default');

          // Only open drawer if not in multi-selection mode
          if (!isMultiSelection) {
            // Check category FIRST
            // Check both product.category and product.category_info?.category for portal imports
            const category = (
              product.category ||
              (product as { category_info?: { category?: string } }).category_info?.category ||
              ''
            ).toLowerCase();
            if (category === 'fixture') {
              setActiveDrawer('fixture');
            } else {
              // For portal-imported controls and all other products, use product drawer
              // The product drawer provides full editing capabilities
              // The control drawer's "Portal Import" view is too limited
              setActiveDrawer('product');
            }
          }
        }
      },
      [
        selectEntity,
        setActiveDrawer,
        setActiveAnnotationType,
        setActiveMode,
        toggleEntitySelection,
        wasRecentlyDragged,
        isPublicView,
        isMultiSelection,
        selectedProducts,
        totalSelections,
      ],
    );

    // Handle mouse enter/leave for hover effects
    const handleMouseEnter = useCallback(
      (product: LVProduct) => {
        if (!isPublicView) {
          setHoveredEntity(product.id, 'product');
        }
      },
      [setHoveredEntity, isPublicView],
    );

    const handleMouseLeave = useCallback(() => {
      if (!isPublicView) {
        setHoveredEntity(null, null);
      }
    }, [setHoveredEntity, isPublicView]);

    // Handle mouse down for dragging
    const handleMouseDown = useCallback(
      (product: LVProduct, e: React.MouseEvent) => {
        // Only start drag on left mouse button
        if (e.button !== 0) return;

        // Don't start drag if we're rotating
        if (rotatingProductId === product.id) return;

        // Check if the product element has the rotating flag
        const productElement = e.currentTarget as HTMLElement;
        if (productElement?.dataset?.rotating === 'true') {
          return;
        }

        // Check if we clicked on the rotation handle area
        const target = e.target as HTMLElement;
        const rotationHandle = target.closest('[data-rotation-handle="true"]');
        if (rotationHandle) {
          return;
        }

        e.preventDefault();
        e.stopPropagation();

        if (!isPublicView && onStartDrag && activeTool === 'select') {
          onStartDrag('product', product.id, e, { x: product.x_position, y: product.y_position });
        }
      },
      [onStartDrag, activeTool, isPublicView, rotatingProductId],
    );

    // Touch event handlers
    const handleTouchStart = useCallback(
      (productId: string, productPos: { x: number; y: number }, e: React.TouchEvent) => {
        // Only handle single touch
        if (e.touches.length !== 1) return;

        e.stopPropagation();

        const touch = e.touches[0];
        const startPos = { x: touch.clientX, y: touch.clientY };
        touchStartPositions.current.set(productId, startPos);

        // Set up press-and-hold timer (500ms)
        const timer = setTimeout(() => {
          // Trigger haptic feedback if available
          if ('vibrate' in navigator) {
            navigator.vibrate(10);
          }

          // Start drag mode
          if (onStartDrag && activeTool === 'select') {
            // Create a synthetic mouse event for compatibility with existing drag handler
            const syntheticEvent = {
              preventDefault: () => {},
              stopPropagation: () => {},
              clientX: touch.clientX,
              clientY: touch.clientY,
              button: 0,
            } as React.MouseEvent;

            onStartDrag('product', productId, syntheticEvent, productPos);

            // Remove the timer since we've started dragging
            touchTimers.current.delete(productId);
          }
        }, 500);

        touchTimers.current.set(productId, timer);
      },
      [onStartDrag, activeTool],
    );

    const handleTouchEnd = useCallback(
      (productId: string, e: React.TouchEvent) => {
        e.stopPropagation();

        // Clear the press-and-hold timer
        const timer = touchTimers.current.get(productId);
        if (timer) {
          clearTimeout(timer);
          touchTimers.current.delete(productId);

          // If timer was cleared, it means we didn't start dragging, so treat as tap
          const startPos = touchStartPositions.current.get(productId);
          if (startPos && e.changedTouches.length > 0) {
            const endTouch = e.changedTouches[0];
            const deltaX = Math.abs(endTouch.clientX - startPos.x);
            const deltaY = Math.abs(endTouch.clientY - startPos.y);

            // If finger didn't move much, treat as tap to select
            if (deltaX < 10 && deltaY < 10) {
              // Find the product to handle click
              const product = products.find(p => p.id === productId);
              if (product) {
                // Create synthetic mouse event for click handler
                const syntheticEvent = {
                  stopPropagation: () => {},
                  preventDefault: () => {},
                  shiftKey: false,
                  button: 0,
                  currentTarget: e.currentTarget,
                  target: e.target,
                } as React.MouseEvent;

                handleProductClick(product, syntheticEvent);
              }
            }
          }
        }

        touchStartPositions.current.delete(productId);
      },
      [handleProductClick, products],
    );

    const handleTouchMove = useCallback((productId: string, e: React.TouchEvent) => {
      // Cancel press-and-hold timer if finger moves too much
      const timer = touchTimers.current.get(productId);
      if (timer) {
        const startPos = touchStartPositions.current.get(productId);
        if (startPos && e.touches.length > 0) {
          const currentTouch = e.touches[0];
          const deltaX = Math.abs(currentTouch.clientX - startPos.x);
          const deltaY = Math.abs(currentTouch.clientY - startPos.y);

          // Cancel timer if moved more than 10 pixels
          if (deltaX > 10 || deltaY > 10) {
            clearTimeout(timer);
            touchTimers.current.delete(productId);
          }
        }
      }
    }, []);

    // Handle rotation
    const handleRotationStart = useCallback(
      (product: LVProduct, e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();

        // Prevent product click and drag
        const productElement = document.querySelector(
          `[data-product-id="${product.id}"]`,
        ) as HTMLElement;
        if (productElement) {
          productElement.dataset.rotating = 'true';
        }

        // Get the product element center, not the rotation handle center
        const productRect = productElement?.getBoundingClientRect();
        if (!productRect) return;

        const centerX = productRect.left + productRect.width / 2;
        const centerY = productRect.top + productRect.height / 2;

        const startAngle = (Math.atan2(e.clientY - centerY, e.clientX - centerX) * 180) / Math.PI;

        setRotatingProductId(product.id);
        const initialRotation = product.rotation || 0;
        setCurrentRotationAngle(initialRotation);

        // Initialize rotation tracking
        rotationRef.current = {
          startAngle,
          startRotation: initialRotation,
          lastAngle: startAngle,
          totalDelta: 0,
        };

        // Store the latest rotation value in closure
        let latestRotation = initialRotation;

        const handleMouseMove = (e: MouseEvent) => {
          if (!rotationRef.current) return;

          const currentAngle =
            (Math.atan2(e.clientY - centerY, e.clientX - centerX) * 180) / Math.PI;

          // Calculate angle difference from last position
          let angleDiff = currentAngle - rotationRef.current.lastAngle;

          // Handle wrapping at -180/180 boundary
          if (angleDiff > 180) {
            angleDiff -= 360;
          } else if (angleDiff < -180) {
            angleDiff += 360;
          }

          // Update total delta
          rotationRef.current.totalDelta += angleDiff;
          rotationRef.current.lastAngle = currentAngle;

          // Calculate new rotation
          const newRotation = rotationRef.current.startRotation + rotationRef.current.totalDelta;

          // Store the raw rotation for saving
          let saveRotation = newRotation % 360;
          if (saveRotation < 0) saveRotation += 360;

          // Apply snapping to display rotation
          let displayRotation = newRotation;
          if (!e.shiftKey) {
            const snapAngles = [0, 45, 90, 135, 180, 225, 270, 315];
            const snapThreshold = 5; // degrees

            const normalizedRotation = ((newRotation % 360) + 360) % 360;
            for (const snapAngle of snapAngles) {
              if (Math.abs(normalizedRotation - snapAngle) < snapThreshold) {
                displayRotation =
                  rotationRef.current.startRotation +
                  rotationRef.current.totalDelta -
                  (normalizedRotation - snapAngle);
                saveRotation = snapAngle;
                break;
              }
            }
          }

          // Store latest rotation for saving
          latestRotation = saveRotation;

          // Update state for real-time display
          setCurrentRotationAngle(saveRotation);

          // Update visual rotation using the display rotation for smooth animation
          const iconContainer = document.querySelector(
            `[data-product-id="${product.id}"] [data-icon-container]`,
          ) as HTMLElement;
          if (iconContainer) {
            // Preserve current scale from DOM (includes base scale)
            const currentTransform = iconContainer.style.transform;
            const scaleMatch = currentTransform.match(/scale\(([\d.]+)\)/);
            const currentScale = scaleMatch
              ? parseFloat(scaleMatch[1])
              : (product.scale || 1.0) * lvProductsBaseScale;
            // Ensure no transition during rotation
            iconContainer.style.transition = 'none';
            iconContainer.style.transform = `scale(${currentScale}) rotate(${displayRotation}deg)`;
          }
        };

        const handleMouseUp = async () => {
          // Get final rotation value from closure
          const finalRotation = latestRotation;

          setRotatingProductId(null);
          rotationRef.current = null;

          // Remove rotating flag
          if (productElement) {
            delete productElement.dataset.rotating;
          }

          // Ensure the visual rotation matches the saved value immediately
          const iconContainer = document.querySelector(
            `[data-product-id="${product.id}"] [data-icon-container]`,
          ) as HTMLElement;
          if (iconContainer) {
            // Preserve current scale from DOM (includes base scale)
            const currentTransform = iconContainer.style.transform;
            const scaleMatch = currentTransform.match(/scale\(([\d.]+)\)/);
            const currentScale = scaleMatch
              ? parseFloat(scaleMatch[1])
              : (product.scale || 1.0) * lvProductsBaseScale;
            // Remove any transition temporarily to prevent animation
            const currentTransition = iconContainer.style.transition;
            iconContainer.style.transition = 'none';
            iconContainer.style.transform = `scale(${currentScale}) rotate(${finalRotation}deg)`;

            // Restore transition after a frame
            requestAnimationFrame(() => {
              iconContainer.style.transition = currentTransition;
            });
          }

          // Save rotation with the final value
          try {
            const oldRotation = product.rotation || 0;

            // Only save if rotation actually changed
            if (Math.abs(oldRotation - finalRotation) > 0.1) {
              await lvProductService.update(product.id, { rotation: finalRotation });

              // Add to undo history
              addAction({
                type: 'ROTATE_ENTITY',
                timestamp: Date.now(),
                floorPlanId: data.floorPlan.id,
                data: {
                  entityType: 'product',
                  id: product.id,
                  oldRotation: oldRotation,
                  newRotation: finalRotation,
                },
              });

              // Delay refetch to prevent visual jumps
              setTimeout(() => {
                data.refetch();
              }, 100);
            }
          } catch (error) {
            logger.error('Failed to save rotation:', error);
            toast.error('Failed to save rotation');
            // Revert visual rotation on error
            if (iconContainer) {
              iconContainer.style.transition = 'none';
              iconContainer.style.transform = `rotate(${product.rotation || 0}deg)`;
            }
          }

          document.removeEventListener('mousemove', handleMouseMove);
          document.removeEventListener('mouseup', handleMouseUp);
          document.removeEventListener('touchmove', handleTouchMove, { passive: false } as any);
          document.removeEventListener('touchend', handleTouchEnd);
        };

        const handleTouchMove = (e: TouchEvent) => {
          if (e.touches.length === 1) {
            handleMouseMove({
              clientX: e.touches[0].clientX,
              clientY: e.touches[0].clientY,
              shiftKey: e.shiftKey,
            } as MouseEvent);
          }
        };

        const handleTouchEnd = () => {
          handleMouseUp();
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('touchmove', handleTouchMove, { passive: false });
        document.addEventListener('touchend', handleTouchEnd);
      },
      [data, addAction],
    );

    // Keyboard shortcuts for rotation (Shift+R) and scaling ([, ]) have been removed per user preference

    return (
      <div
        className="absolute inset-0"
        style={{
          pointerEvents: 'none',
        }}
      >
        {/* Render products - using pre-filtered renderableProducts */}
        {renderableProducts.map(product => {
          const isSelected = selectedProducts.has(product.id);
          const isHovered = hoveredEntityId === product.id && hoveredEntityType === 'product';
          const isBeingDragged = dragState?.type === 'product' && dragState.id === product.id;
          const isPresentationSelected = presentationLVProductIds.includes(product.id);

          // Get room info for the product
          const room = product.room_id ? data.rooms?.find(r => r.id === product.room_id) : null;

          const isGroup = product.category === 'group';
          // Check if product is a control (portal-imported control)
          const productCategory = product.category?.toLowerCase();
          const isControl = productCategory === 'control' || productCategory === 'controls';

          return (
            <div
              key={product.id}
              data-product-id={product.id}
              data-width={product.width || 40}
              data-height={product.height || 40}
              className={cn('absolute transition-all duration-200 select-none', {
                'cursor-pointer': true,
                'cursor-grab': !isDragging && !isBeingDragged && activeTool === 'select',
                'cursor-grabbing': isBeingDragged,
                'shadow-lg z-[100]': isBeingDragged,
                // Controls don't scale on selection, others do
                'scale-110': isSelected && !isBeingDragged && !isControl,
                'opacity-50': isDragging && !isBeingDragged,
              })}
              style={{
                left: product.x_position,
                top: product.y_position,
                pointerEvents: 'auto',
                transform: 'translate(-50%, -50%)',
                zIndex: isBeingDragged ? 200 : isSelected ? 100 : isHovered ? 50 : 10,
                backgroundColor: 'transparent',
              }}
              onClick={e => handleProductClick(product, e)}
              onMouseEnter={() => handleMouseEnter(product)}
              onMouseLeave={handleMouseLeave}
              onMouseDown={e => handleMouseDown(product, e)}
              onTouchStart={e =>
                handleTouchStart(product.id, { x: product.x_position, y: product.y_position }, e)
              }
              onTouchEnd={e => handleTouchEnd(product.id, e)}
              onTouchMove={e => handleTouchMove(product.id, e)}
              onContextMenu={e => e.preventDefault()}
            >
              {/* Product icon container - square for groups, round for products */}
              <div
                data-icon-container
                style={{
                  transform: `scale(${(product.scale || 1.0) * lvProductsBaseScale}) rotate(${product.rotation || 0}deg)`,
                  transition: rotatingProductId === product.id ? 'none' : 'transform 0.2s',
                }}
              >
                {iconStyle === 'high-contrast' ? (
                  // High contrast mode with colored backgrounds
                  [
                    'speaker',
                    'camera',
                    'access_point',
                    'touch_panel',
                    'doorbell',
                    'thermostat',
                    'data',
                    'sensor',
                    'shades',
                  ].includes(product.category) ? (
                    // For custom SVG icons - enhanced design with solid background
                    <div
                      className={cn(
                        'relative flex items-center justify-center transition-all shadow-sm',
                        isGroup ? 'rounded-lg' : 'rounded-full',
                        isHovered && 'ring-2 ring-offset-1 ring-gray-400',
                      )}
                      style={{
                        width: '28px',
                        height: '28px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor:
                          product.category === 'speaker'
                            ? isSelected
                              ? '#b45309'
                              : '#d97706' // orange-700/600
                            : product.category === 'camera'
                              ? isSelected
                                ? '#4338ca'
                                : '#4f46e5' // indigo-700/600
                              : product.category === 'access_point'
                                ? isSelected
                                  ? '#047857'
                                  : '#059669' // emerald-700/600
                                : product.category === 'touch_panel'
                                  ? isSelected
                                    ? '#0369a1'
                                    : '#0284c7' // sky-700/600
                                  : product.category === 'doorbell'
                                    ? isSelected
                                      ? '#7e22ce'
                                      : '#9333ea' // purple-700/600
                                    : product.category === 'thermostat'
                                      ? isSelected
                                        ? '#334155'
                                        : '#475569' // slate-700/600
                                      : product.category === 'data'
                                        ? isSelected
                                          ? '#1d4ed8'
                                          : '#2563eb' // blue-700/600
                                        : product.category === 'sensor'
                                          ? isSelected
                                            ? '#b45309'
                                            : '#d97706' // orange-700/600
                                          : product.category === 'shades'
                                            ? isSelected
                                              ? '#374151'
                                              : '#4b5563' // gray-700/600
                                            : isSelected
                                              ? '#374151'
                                              : '#4b5563', // default gray-700/600
                        filter: isSelected ? 'brightness(1.1)' : 'brightness(1)',
                      }}
                    >
                      <ProductIcon
                        key={`${product.category}-${product.subcategory || 'none'}`}
                        product={product}
                        size={20}
                        className=""
                        applyHighContrastFilter={true}
                        preloadedIconData={getPreloadedProductIcon(product)}
                      />
                    </div>
                  ) : (
                    // For other products - enhanced standard container
                    <div
                      className={cn(
                        'relative flex items-center justify-center transition-all shadow-sm',
                        isGroup ? 'rounded-lg' : 'rounded-full',
                        isHovered && 'ring-2 ring-offset-1 ring-gray-400',
                      )}
                      style={{
                        width: '28px',
                        height: '28px',
                        // Default gray for uncategorized items
                        backgroundColor: isSelected ? '#374151' : '#4b5563', // gray-700/600
                      }}
                    >
                      <ProductIcon
                        key={`${product.category}-${product.subcategory || 'none'}`}
                        product={product}
                        size={18}
                        className={cn('transition-colors text-white')}
                        preloadedIconData={getPreloadedProductIcon(product)}
                      />
                    </div>
                  )
                ) : // Minimalist mode - original SVG style with white background
                [
                    'speaker',
                    'camera',
                    'access_point',
                    'touch_panel',
                    'doorbell',
                    'thermostat',
                    'data',
                    'sensor',
                    'shades',
                  ].includes(product.category) ? (
                  // For custom SVG icons - render with transparent background
                  <div
                    className={cn(
                      'relative flex items-center justify-center transition-all shadow-sm',
                      isGroup ? 'rounded-lg' : 'rounded-full',
                      isSelected && 'ring-2 ring-gray-400',
                      isHovered && 'ring-1 ring-gray-300',
                    )}
                    style={{
                      width: '28px',
                      height: '28px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ProductIcon
                      key={`${product.category}-${product.subcategory || 'none'}`}
                      product={product}
                      size={22}
                      className=""
                      preloadedIconData={getPreloadedProductIcon(product)}
                    />
                  </div>
                ) : (
                  // For other products (group, controls, etc.) - transparent background style
                  <div
                    className={cn(
                      'relative flex items-center justify-center transition-all shadow-sm',
                      // Controls use rounded-sm (rectangular), groups use rounded-lg, others use rounded-full
                      isGroup ? 'rounded-lg' : isControl ? 'rounded-sm' : 'rounded-full',
                      // Controls use thinner ring-1, others use ring-2
                      isSelected && (isControl ? 'ring-1 ring-gray-400' : 'ring-2 ring-gray-400'),
                      isHovered && 'ring-1 ring-gray-300',
                    )}
                    style={{
                      width: '28px',
                      height: '28px',
                    }}
                  >
                    <ProductIcon
                      key={`${product.category}-${product.subcategory || 'none'}`}
                      product={product}
                      size={18}
                      className={cn(
                        'transition-colors',
                        isSelected ? 'text-gray-800' : 'text-gray-600',
                      )}
                      preloadedIconData={getPreloadedProductIcon(product)}
                    />
                  </div>
                )}
              </div>

              {/* Rotation handle for selected products */}
              {isSelected &&
                !isPublicView &&
                !isBeingDragged &&
                (() => {
                  // Calculate offset: icon radius * scale + fixed margin
                  // Read actual scale from DOM to account for live resize changes (includes base scale)
                  let currentProductScale = (product.scale || 1.0) * lvProductsBaseScale;
                  const iconContainer = document.querySelector(
                    `[data-product-id="${product.id}"] [data-icon-container]`,
                  ) as HTMLElement;
                  if (iconContainer && iconContainer.style.transform) {
                    const scaleMatch = iconContainer.style.transform.match(/scale\(([\d.]+)\)/);
                    if (scaleMatch) {
                      currentProductScale = parseFloat(scaleMatch[1]);
                    }
                  }

                  const iconRadius = 24; // base icon radius in pixels
                  const margin = 11; // fixed margin from icon edge
                  const calculatedOffset = iconRadius * currentProductScale + margin;
                  // Ensure minimum offset for very small scales
                  const rotationOffset = Math.max(calculatedOffset, 35);

                  return (
                    <div
                      className="absolute"
                      data-rotation-handle="true"
                      data-product-rotation-handle={product.id}
                      style={{
                        left: '50%',
                        top: `-${rotationOffset}px`,
                        transform: 'translateX(-50%)',
                        pointerEvents: 'auto',
                        zIndex: 1000,
                      }}
                    >
                      {/* Rotation handle button */}
                      <div
                        className="bg-white rounded-full shadow-lg border border-blue-400/80 cursor-grab hover:scale-110 hover:border-blue-500 hover:shadow-xl transition-all flex items-center justify-center"
                        style={{
                          width: '20px',
                          height: '20px',
                        }}
                        onMouseDown={e => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleRotationStart(product, e);
                        }}
                        onClick={e => {
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                        onPointerDown={e => {
                          e.stopPropagation();
                        }}
                        onTouchStart={e => {
                          e.stopPropagation();
                          e.preventDefault();
                          const touch = e.touches[0];
                          const mouseEvent = new MouseEvent('mousedown', {
                            clientX: touch.clientX,
                            clientY: touch.clientY,
                            bubbles: true,
                          }) as any;
                          handleRotationStart(product, mouseEvent);
                        }}
                      >
                        {/* Rotation icon - elegant curved arrow */}
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          className="text-blue-500"
                        >
                          <path
                            d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C15.0902 3 17.7902 4.58073 19.3499 7"
                            stroke="currentColor"
                            strokeWidth={1.8}
                            strokeLinecap="round"
                          />
                          <path
                            d="M17 7L19.5 7L19.5 4.5"
                            stroke="currentColor"
                            strokeWidth={1.8}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>

                      {/* Angle indicator when rotating - positioned to the side to avoid icon/label interference */}
                      {rotatingProductId === product.id && (
                        <div
                          className="absolute px-2 py-1 rounded text-xs"
                          style={{
                            backgroundColor: 'rgba(37, 99, 235, 0.9)',
                            whiteSpace: 'nowrap',
                            left: '35px',
                            top: '-5px',
                            pointerEvents: 'none',
                          }}
                        >
                          <span className="text-white font-semibold">
                            {Math.round(currentRotationAngle)}°
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })()}

              {/* Product name label - plain text below icon */}
              {showProductLabels &&
                (() => {
                  // Get current scale - read from DOM for live updates (includes base scale)
                  let currentProductScale = (product.scale || 1.0) * lvProductsBaseScale;
                  const iconContainer = document.querySelector(
                    `[data-product-id="${product.id}"] [data-icon-container]`,
                  ) as HTMLElement;
                  if (iconContainer && iconContainer.style.transform) {
                    const scaleMatch = iconContainer.style.transform.match(/scale\(([\d.]+)\)/);
                    if (scaleMatch) {
                      currentProductScale = parseFloat(scaleMatch[1]);
                    }
                  }

                  // Label moves down proportionally with scale
                  const labelMarginTop = 6 + (currentProductScale - 1.0) * 10;

                  return (
                    <div
                      data-product-label={product.id}
                      className="absolute left-0 top-full text-xs font-medium whitespace-nowrap pointer-events-none text-gray-700"
                      style={{
                        fontSize: `${productLabelFontSize}px`,
                        marginTop: `${labelMarginTop}px`,
                        transform: 'translateX(-50%)',
                        transformOrigin: 'top center',
                        left: '50%',
                      }}
                    >
                      {/* Use display_label from category_info if available, otherwise show Category: Subcategory for generic products */}
                      {product.category_info?.display_label ||
                        (product.category_info?.is_generic && product.category_info?.subcategory
                          ? `${product.category_info.category}: ${product.category_info.subcategory}`
                          : product.name)}
                    </div>
                  );
                })()}

              {/* Show presentation mode selection indicator - pulse ring effect */}
              {isPresentationSelected && (
                <>
                  {/* Primary ring */}
                  <div className="absolute -inset-2 border-2 border-primary rounded-full pointer-events-none animate-pulse-ring" />
                  {/* Secondary ring - delayed */}
                  <div
                    className="absolute -inset-2 border-2 border-primary rounded-full pointer-events-none animate-pulse-ring"
                    style={{
                      animationDelay: '0.5s',
                    }}
                  />
                  {/* Static border for visibility */}
                  <div className="absolute -inset-1 border-2 border-primary/60 rounded-full pointer-events-none" />
                </>
              )}
            </div>
          );
        })}

        {/* Render product image callouts for products with show_product_image enabled */}
        {products
          .filter(
            product => !product.parent_group_id && product.show_product_image && product.product_id, // Must have a linked product
          )
          .map(product => (
            <ProductCalloutWrapper
              key={`callout-${product.id}`}
              product={product}
              scale={scale}
              isSelected={selectedProducts.has(product.id)}
              isPublicView={isPublicView || false}
              onRefetch={data.refetch}
              dragState={dragState}
            />
          ))}
      </div>
    );
  },
);

LVProductLayer.displayName = 'LVProductLayer';

/**
 * Wrapper component to render a product image callout
 * Fetches the product's linked library item to get the image URL
 */
const ProductCalloutWrapper = memo(
  ({
    product,
    scale,
    isSelected,
    isPublicView,
    onRefetch,
    dragState,
  }: {
    product: LVProduct;
    scale: number;
    isSelected: boolean;
    isPublicView: boolean;
    onRefetch: () => void;
    dragState?: DragState | null;
  }) => {
    const { data: linkedProduct } = useProduct(product.product_id || undefined);

    // Cache the last known icon position when drag ends to prevent line flickering
    // This ref keeps the position until the database update propagates
    const lastKnownIconPosRef = useRef<{ x: number; y: number } | null>(null);
    const wasBeingDraggedRef = useRef(false);

    // Get image URL from various sources
    const imageUrl =
      linkedProduct?.image_url ||
      (linkedProduct?.portal_metadata as any)?.imageUrl ||
      (product.metadata as any)?.portal_image_url;

    if (!imageUrl) {
      return null;
    }

    // Calculate live icon position if this product is being dragged
    // Only use currentPos once drag threshold is crossed (hasMovedThreshold = true)
    // Before threshold, currentPos might be the click position, not element center
    const isBeingDragged = dragState?.type === 'product' && dragState.id === product.id;
    const liveIconPosition =
      isBeingDragged && dragState?.hasMovedThreshold && dragState?.currentPos
        ? { x: dragState.currentPos.x, y: dragState.currentPos.y }
        : undefined;

    // Track drag state transitions and cache final position
    if (isBeingDragged && liveIconPosition) {
      // Currently dragging - update cached position
      lastKnownIconPosRef.current = liveIconPosition;
      wasBeingDraggedRef.current = true;
    } else if (wasBeingDraggedRef.current && !isBeingDragged) {
      // Just finished dragging - keep using cached position until DB updates
      // The cached position will be cleared when product.x_position/y_position update
    }

    // Clear cache when product position updates to match the cached position
    if (lastKnownIconPosRef.current) {
      const cachedPos = lastKnownIconPosRef.current;
      const distance = Math.sqrt(
        Math.pow(cachedPos.x - product.x_position, 2) +
          Math.pow(cachedPos.y - product.y_position, 2),
      );
      if (distance < 5) {
        // DB has updated, clear cache
        lastKnownIconPosRef.current = null;
        wasBeingDraggedRef.current = false;
      }
    }

    // Use live position during drag, cached position briefly after drag ends, then DB position
    const iconPosition =
      liveIconPosition ?? (wasBeingDraggedRef.current ? lastKnownIconPosRef.current : undefined);

    const handlePositionChange = async (x: number, y: number) => {
      try {
        await lvProductService.update(product.id, {
          product_image_x: x,
          product_image_y: y,
        });
        onRefetch();
      } catch (error) {
        logger.error('Failed to update image position:', error);
        toast.error('Failed to update image position');
      }
    };

    const handleSizeChange = async (width: number, height: number) => {
      try {
        await lvProductService.update(product.id, {
          product_image_width: width,
          product_image_height: height,
        });
        onRefetch();
      } catch (error) {
        logger.error('Failed to update image size:', error);
        toast.error('Failed to update image size');
      }
    };

    const handleClose = async () => {
      try {
        await lvProductService.update(product.id, {
          show_product_image: false,
        });
        onRefetch();
      } catch (error) {
        logger.error('Failed to hide image callout:', error);
        toast.error('Failed to hide image callout');
      }
    };

    return (
      <ProductImageCallout
        product={product}
        imageUrl={imageUrl}
        scale={scale}
        isSelected={isSelected}
        isPublicView={isPublicView}
        onPositionChange={handlePositionChange}
        onSizeChange={handleSizeChange}
        onClose={handleClose}
        iconPosition={iconPosition}
      />
    );
  },
);

ProductCalloutWrapper.displayName = 'ProductCalloutWrapper';
